import * as Yup from "yup";
import { VerticalSpacing } from "../../VerticalSpacing";
import { V3TextInput } from "../../TextInput/V3TextInput";
import { RightAlignPanel } from "../../ReusableStyledComponents/RightAlignPanel";
import { V3Button } from "../../Button";
import { V3CaptionGrey35 } from "../../ReusableStyledComponents/V3CaptionGrey35";
import { useFormik } from "formik";
import { YupNameValidation, YupSourceValidation } from "../../../shared-logic/validation-utils";
import { V3Select } from "../../Select";
import { useRef, useState } from "react";
import { V3Loader } from "../../Loader";
import { V3ButtonColor } from "../../Button/V3Button.types";
import { V3BodyGrey35 } from "../../ReusableStyledComponents/V3BodyGrey35";
import { InlineLink } from "../../ReusableStyledComponents/InlineLink";

interface V3AddContentEmailFormik {
    name: string;
    newSource: boolean;
    sourceSlug?: string;
}

interface AddContentEmailProps {
    sourcesOptions: { value: string; text: string | null }[];
    onBackHandler: () => void;
    onFilesChoosen: (files: File[]) => void;
    onAddContentEmailFormSubmit: (data: V3AddContentEmailFormik) => Promise<void>;
}

export const AddContentEmail = ({ onBackHandler, onAddContentEmailFormSubmit, sourcesOptions, onFilesChoosen }: AddContentEmailProps) => {
    const [actionSubmitting, setActionSubmitting] = useState(false);
    const inputFileRef = useRef<HTMLInputElement | null>(null);

    function handleFileSelected(selectorFiles: FileList): void {
        onFilesChoosen(Array.from(selectorFiles));
        if (inputFileRef && inputFileRef.current) inputFileRef.current.value = "";
    }

    const confirmImportFormik = useFormik<V3AddContentEmailFormik>({
        initialValues: {
            name: "",
            sourceSlug: undefined,
            newSource: false
        },
        onSubmit: async (data) => {
            setActionSubmitting(true);

            try {
                await onAddContentEmailFormSubmit(data);
            } catch {}

            setActionSubmitting(false);
        },
        validationSchema: Yup.object().shape({
            name: YupNameValidation,
            sourceSlug: YupSourceValidation
        }),
        enableReinitialize: true
    });

    return (
        <>
            <VerticalSpacing bottom={24}>
                <V3CaptionGrey35>
                    Adding emails is super easy: Every source in your Authory account comes with a secret email address. Forward any email to this
                    address and a screenshot of the email will automatically appear in your account.
                </V3CaptionGrey35>
            </VerticalSpacing>
            <form onSubmit={confirmImportFormik.handleSubmit}>
                <VerticalSpacing bottom={23}>
                    {!confirmImportFormik.values.newSource ? (
                        <V3Select
                            label="Show secret email for this source"
                            name="sourceSlug"
                            value={confirmImportFormik.values.sourceSlug}
                            onChange={(e) => {
                                confirmImportFormik.handleChange(e);
                                setTimeout(() => {
                                    confirmImportFormik.submitForm();
                                }, 70);
                            }}
                            options={sourcesOptions}
                            actionText={"Create new source"}
                            disabledOption="Please select"
                            actionClickHandler={() => confirmImportFormik.setFieldValue("newSource", true)}
                            error={
                                confirmImportFormik.errors.hasOwnProperty("sourceSlug") && confirmImportFormik.touched.hasOwnProperty("sourceSlug")
                                    ? confirmImportFormik.errors.sourceSlug
                                    : undefined
                            }
                        />
                    ) : (
                        <V3TextInput
                            name="name"
                            label="Name"
                            placeholder={"e.g. Apple Newsletter"}
                            onChange={confirmImportFormik.handleChange}
                            onBlur={confirmImportFormik.handleBlur}
                            value={confirmImportFormik.values.name}
                            actionText="Content is from existing source"
                            actionClickHandler={() => confirmImportFormik.setFieldValue("newSource", false)}
                            error={
                                confirmImportFormik.errors.hasOwnProperty("name") && confirmImportFormik.touched.hasOwnProperty("name")
                                    ? confirmImportFormik.errors.name
                                    : undefined
                            }
                        />
                    )}
                </VerticalSpacing>
                <input
                    style={{ display: "none" }}
                    type="file"
                    ref={inputFileRef}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => e.target.files && handleFileSelected(e.target.files)}
                    accept={".png,.jpg"}
                    multiple
                />
                <VerticalSpacing top={32}>
                    <V3BodyGrey35>
                        Alternatively you can{" "}
                        <InlineLink onClick={() => inputFileRef && inputFileRef.current && inputFileRef.current.click()}>
                            upload a screenshot
                        </InlineLink>
                        .
                    </V3BodyGrey35>
                </VerticalSpacing>
                <RightAlignPanel>
                    <div>
                        {!actionSubmitting && (
                            <V3Button
                                type="button"
                                text="Back"
                                color={V3ButtonColor.secondary}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onBackHandler();
                                }}
                            />
                        )}
                    </div>
                    <div>
                        {actionSubmitting ? (
                            <V3Loader height={42} />
                        ) : (
                            <V3Button text={"Continue"} type="submit" disabled={actionSubmitting} autoWidth />
                        )}
                    </div>
                </RightAlignPanel>
            </form>
        </>
    );
};
